import { useMemo, useState } from "react";
import { DEFAULT_INFRA_PRICE_TABLE, estimateMonthlyHostingCost, revenueScenarios } from "@dbindex/pricing";
import { FRESHNESS_SECONDS, parseDecimalToMicros, type Freshness } from "@dbindex/contracts";
import { Field, Input, Select } from "../ui";
import { formatMoney, formatNumber } from "../../lib/format";

const FRESHNESS_OPTIONS: { value: Freshness; label: string }[] = [
  { value: "1s", label: "1 second" },
  { value: "5s", label: "5 seconds" },
  { value: "10s", label: "10 seconds" },
  { value: "30s", label: "30 seconds" },
  { value: "1m", label: "1 minute" },
  { value: "5m", label: "5 minutes" },
  { value: "15m", label: "15 minutes" },
  { value: "1h", label: "1 hour" },
  { value: "1d", label: "Daily" },
];

const AVG_ROW_BYTES = 400;
const GB = 1024 ** 3;
const SHARD_TARGET_BYTES = 2 * GB;
const MINUTES_PER_MONTH = 60 * 24 * 30;

function safeMicros(s: string): number | null {
  try {
    const m = parseDecimalToMicros(s);
    return m >= 0 ? m : null;
  } catch {
    return null;
  }
}

/**
 * Interactive provider economics (spec §49). All money is integer micros; the hosting
 * estimate comes from the versioned infrastructure price table in @dbindex/pricing.
 */
export function RevenueCalculator() {
  const [sizeGb, setSizeGb] = useState("2");
  const [freshness, setFreshness] = useState<Freshness>("10s");
  const [changes, setChanges] = useState("5");
  const [qpm, setQpm] = useState("100");
  const [price, setPrice] = useState("0.0012");
  const [cache, setCache] = useState("60");
  const [fee, setFee] = useState("20");

  const parsed = useMemo(() => {
    const size = Number(sizeGb);
    const q = Number(qpm);
    const ch = Number(changes);
    const c = Number(cache);
    const f = Number(fee);
    const p = safeMicros(price);
    const errors: Record<string, string> = {};
    if (!Number.isFinite(size) || size < 0 || size > 10_000) errors.size = "0 – 10,000 GB";
    if (!Number.isFinite(q) || q < 0 || q > 10_000_000) errors.qpm = "0 – 10M queries/min";
    if (!Number.isFinite(ch) || ch < 0 || ch > 1_000_000) errors.changes = "0 – 1M rows/s";
    if (!Number.isFinite(c) || c < 0 || c > 100) errors.cache = "0 – 100%";
    if (!Number.isFinite(f) || f < 0 || f > 100) errors.fee = "0 – 100%";
    if (p === null) errors.price = "Decimal USD, up to 6 places";
    return { size, q, ch, c, f, p, errors };
  }, [sizeGb, qpm, changes, cache, fee, price]);

  const result = useMemo(() => {
    const { size, q, ch, c, f, p, errors } = parsed;
    if (Object.keys(errors).length > 0 || p === null) return null;
    const bytes = size * GB;
    const hosting = estimateMonthlyHostingCost(
      {
        rowCount: Math.round(bytes / AVG_ROW_BYTES),
        avgRowBytes: AVG_ROW_BYTES,
        changesPerSecond: ch,
        freshnessSeconds: FRESHNESS_SECONDS[freshness],
        queriesPerMinute: q,
        cacheHitRatio: c / 100,
        shards: Math.max(1, Math.ceil(bytes / SHARD_TARGET_BYTES)),
        semanticIndex: false,
      },
      DEFAULT_INFRA_PRICE_TABLE,
    );
    const [row] = revenueScenarios({ pricePerQueryMicros: p, feeBps: Math.round(f * 100), hosting, qpms: [q] });
    return { hosting, row };
  }, [parsed, freshness]);

  const out = result?.row;
  return (
    <div className="grid gap-px overflow-hidden rounded-xl border border-border bg-border lg:grid-cols-[1fr_1.1fr]">
      <form className="grid gap-4 bg-panel p-6 sm:grid-cols-2" onSubmit={(e) => e.preventDefault()} aria-label="Revenue calculator inputs">
        <Field label="Serving projection size (GB)" error={parsed.errors.size}>
          {(p) => <Input {...p} inputMode="decimal" value={sizeGb} onChange={(e) => setSizeGb(e.target.value)} />}
        </Field>
        <Field label="Freshness target">{(p) => <Select {...p} value={freshness} onChange={(e) => setFreshness(e.target.value as Freshness)} options={FRESHNESS_OPTIONS} />}</Field>
        <Field label="Source changes (rows/sec)" error={parsed.errors.changes}>
          {(p) => <Input {...p} inputMode="decimal" value={changes} onChange={(e) => setChanges(e.target.value)} />}
        </Field>
        <Field label="Expected queries per minute" error={parsed.errors.qpm}>
          {(p) => <Input {...p} inputMode="numeric" value={qpm} onChange={(e) => setQpm(e.target.value)} />}
        </Field>
        <Field label="Price per query (USD)" error={parsed.errors.price}>
          {(p) => <Input {...p} inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} />}
        </Field>
        <Field label="Cache hit ratio (%)" error={parsed.errors.cache}>
          {(p) => <Input {...p} inputMode="decimal" value={cache} onChange={(e) => setCache(e.target.value)} />}
        </Field>
        <Field label="DBIndex marketplace fee (%)" error={parsed.errors.fee} className="sm:col-span-2">
          {(p) => <Input {...p} inputMode="decimal" value={fee} onChange={(e) => setFee(e.target.value)} />}
        </Field>
      </form>
      <div className="bg-bg-elev p-6" aria-live="polite">
        {out && result ? (
          <>
            <dl className="grid grid-cols-2 gap-x-6 gap-y-4">
              <Stat label="Monthly queries" value={formatNumber(out.queriesPerMonth)} />
              <Stat label="Consumer spend" value={formatMoney(out.grossMicros)} />
              <Stat label="DBIndex fee" value={formatMoney(out.feeMicros)} />
              <Stat label="Gross provider revenue" value={formatMoney(out.providerGrossMicros)} />
              <Stat label="Estimated infrastructure" value={`${formatMoney(result.hosting.lowMicros)} – ${formatMoney(result.hosting.highMicros)}`} />
              <Stat label="Estimated provider net" value={formatMoney(out.netMicros)} emphasis />
            </dl>
            <details className="mt-6 rounded-md border border-border bg-panel p-3 text-xs text-fg-2">
              <summary className="cursor-pointer text-fg">Assumptions</summary>
              <ul className="mt-2 list-disc space-y-1 pl-4">
                <li>
                  {formatNumber(MINUTES_PER_MONTH)} minutes per month; queries/month = QPM × {formatNumber(MINUTES_PER_MONTH)}.
                </li>
                <li>Average serving row of {AVG_ROW_BYTES} bytes; one serving shard per 2 GB.</li>
                <li>Every logical query is charged to the consumer, including cache hits; caching lowers infrastructure cost, not price.</li>
                <li>Provider net = gross − marketplace fee − estimated hosting (expected case: {formatMoney(result.hosting.expectedMicros)}).</li>
                <li>Infrastructure price table version {result.hosting.tableVersion}. Prices are updated without deployment.</li>
                {result.hosting.assumptions.map((a) => (
                  <li key={a}>{a}</li>
                ))}
                <li>This is an estimate, not a guarantee of demand or income.</li>
              </ul>
            </details>
          </>
        ) : (
          <p className="text-sm text-fg-3">Fix the highlighted inputs to see an estimate.</p>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, emphasis }: { label: string; value: string; emphasis?: boolean }) {
  return (
    <div>
      <dt className="text-xs text-fg-3">{label}</dt>
      <dd className={emphasis ? "tabular mt-1 text-2xl font-semibold text-accent" : "tabular mt-1 text-lg font-medium text-fg"}>{value}</dd>
    </div>
  );
}
